import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '../../../../components/ui/Dialog';
import Button from '../../../../components/ui/Button';
import FilePreviewPanel from './FilePreviewPanel';
import DocumentFormPanel from './DocumentFormPanel';
import FileDropZone from './FileDropZone';
import ProfileUpdatesPanel, { ProfileUpdateRow } from './ProfileUpdatesPanel';
import { useOcrAnalysis } from '../../../../hooks/useOcrAnalysis';
import { UserAttachmentInput, CompanyAttachmentTypeView, UserView } from '../../../../models/general';
import { OcrSettingsView } from '../../../../api/General/ocrSettings';
import { getUser } from '../../../../api/General/account';
import { getCompanyAttachmentTypes } from '../../../../api/General/attachment';
import { apiDateOnlyParam } from '@/utils/apiDate';

interface FileEntry {
  file: File;
  label: string;
}

interface DocumentUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (input: UserAttachmentInput, additionalFiles: FileEntry[]) => Promise<void>;
  loading: boolean;
  title: string;
  userId: string;
  ocrSettings: OcrSettingsView | null;
  initialFiles?: File[];
  editData?: {
    id: number;
    fileName?: string;
    description?: string;
    expiryDate?: string;
    reminderDays?: number;
    companyAttachmentTypeId?: number;
    documentNumber?: string;
    issueDate?: string;
  };
}

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  open,
  onClose,
  onSave,
  loading,
  title,
  userId,
  ocrSettings,
  initialFiles,
  editData,
}) => {
  const { t } = useTranslation();
  const { analyzing, result: ocrResult, analyze, reset: resetOcr } = useOcrAnalysis();

  const [primaryFile, setPrimaryFile] = useState<File | null>(null);
  const [additionalFiles, setAdditionalFiles] = useState<FileEntry[]>([]);
  const [skipFile, setSkipFile] = useState(false);
  const [skipProfileUpdates, setSkipProfileUpdates] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserView | null>(null);
  const [attachmentTypes, setAttachmentTypes] = useState<CompanyAttachmentTypeView[]>([]);
  const [formData, setFormData] = useState({
    fileName: editData?.fileName || '',
    description: editData?.description || '',
    expiryDate: editData?.expiryDate,
    reminderDays: editData?.reminderDays ?? 0,
    companyAttachmentTypeId: editData?.companyAttachmentTypeId,
    documentNumber: editData?.documentNumber || '',
    issueDate: editData?.issueDate,
  });

  // Fetch user profile and attachment types when modal opens
  useEffect(() => {
    if (open && userId) {
      getUser(userId).then(res => {
        if (res?.payload) setCurrentUser(res.payload);
      });
      getCompanyAttachmentTypes().then(res => {
        if (res?.payload) setAttachmentTypes(res.payload);
      });
    }
  }, [open, userId]);

  // Handle initial files from drag-and-drop
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0 && open) {
      const [first, ...rest] = initialFiles;
      setPrimaryFile(first);
      setAdditionalFiles(rest.map(f => ({ file: f, label: '' })));

      // Auto-fill file name from primary file
      const nameWithoutExt = first.name.replace(/\.[^/.]+$/, '');
      setFormData(prev => ({ ...prev, fileName: nameWithoutExt }));

      // Trigger OCR if enabled
      if (ocrSettings?.ocrEnabled) {
        analyze(first);
      }
    }
  }, [initialFiles, open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setPrimaryFile(null);
      setAdditionalFiles([]);
      setSkipFile(false);
      setSkipProfileUpdates(false);
      setCurrentUser(null);
      setFormData({
        fileName: '',
        description: '',
        expiryDate: undefined,
        reminderDays: 0,
        companyAttachmentTypeId: undefined,
        documentNumber: '',
        issueDate: undefined,
      });
      resetOcr();
    }
  }, [open]);

  // Re-populate form data when editing
  useEffect(() => {
    if (editData && open) {
      setFormData({
        fileName: editData.fileName || '',
        description: editData.description || '',
        expiryDate: editData.expiryDate,
        reminderDays: editData.reminderDays ?? 0,
        companyAttachmentTypeId: editData.companyAttachmentTypeId,
        documentNumber: editData.documentNumber || '',
        issueDate: editData.issueDate,
      });
    }
  }, [editData, open]);

  const handleFilesSelected = useCallback((files: File[]) => {
    if (!primaryFile) {
      const [first, ...rest] = files;
      setPrimaryFile(first);
      setAdditionalFiles(prev => [...prev, ...rest.map(f => ({ file: f, label: '' }))]);

      const nameWithoutExt = first.name.replace(/\.[^/.]+$/, '');
      setFormData(prev => ({ ...prev, fileName: nameWithoutExt }));

      if (ocrSettings?.ocrEnabled) {
        analyze(first);
      }
    } else {
      setAdditionalFiles(prev => [...prev, ...files.map(f => ({ file: f, label: '' }))]);
    }
  }, [primaryFile, ocrSettings, analyze]);

  const handleRemoveAdditional = useCallback((index: number) => {
    setAdditionalFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleLabelChange = useCallback((index: number, label: string) => {
    setAdditionalFiles(prev => prev.map((entry, i) => i === index ? { ...entry, label } : entry));
  }, []);

  const handleSave = async () => {
    const input: UserAttachmentInput = {
      file: primaryFile,
      fileName: formData.fileName,
      expiryDate: formData.expiryDate,
      reminderDays: formData.reminderDays,
      userId,
      companyAttachmentTypeId: formData.companyAttachmentTypeId,
      description: formData.description,
      documentNumber: formData.documentNumber,
      issueDate: formData.issueDate,
      additionalFiles: additionalFiles.map(af => af.file),
      additionalFileLabels: additionalFiles.map(af => af.label),
      skipProfileUpdates,
    };
    await onSave(input, additionalFiles);
  };

  // Compute profile update rows by comparing OCR / form data against current user
  const profileUpdates = useMemo<ProfileUpdateRow[]>(() => {
    if (!currentUser) return [];
    const updates: ProfileUpdateRow[] = [];

    // Look up the selected attachment type name
    const selectedType = attachmentTypes.find(at => at.id === formData.companyAttachmentTypeId);
    const typeName = (selectedType?.attachmentType || '').toLowerCase();

    // Document number -> identity field based on type
    if (formData.documentNumber) {
      if (typeName.includes('passport') && formData.documentNumber !== currentUser.passportNumber) {
        updates.push({
          fieldLabel: t('passportNumber'),
          currentValue: currentUser.passportNumber || '',
          newValue: formData.documentNumber,
          isReplacing: !!currentUser.passportNumber,
        });
      } else if ((typeName.includes('emirates') || typeName.includes('eid')) && formData.documentNumber !== currentUser.emiratesIdNumber) {
        updates.push({
          fieldLabel: t('emiratesIdNumber'),
          currentValue: currentUser.emiratesIdNumber || '',
          newValue: formData.documentNumber,
          isReplacing: !!currentUser.emiratesIdNumber,
        });
      } else if ((typeName.includes('labour') || typeName.includes('labor')) && formData.documentNumber !== currentUser.labourCardNumber) {
        updates.push({
          fieldLabel: t('labourCardNumber'),
          currentValue: currentUser.labourCardNumber || '',
          newValue: formData.documentNumber,
          isReplacing: !!currentUser.labourCardNumber,
        });
      } else if (typeName.includes('visa') && formData.documentNumber !== currentUser.visaIdNumber) {
        updates.push({
          fieldLabel: t('visaIdNumber'),
          currentValue: currentUser.visaIdNumber || '',
          newValue: formData.documentNumber,
          isReplacing: !!currentUser.visaIdNumber,
        });
      }
    }

    // Expiry date -> identity expiry based on type
    if (formData.expiryDate) {
      const formatDate = (d: Date | string | undefined | null) => {
        return apiDateOnlyParam(d);
      };

      if (typeName.includes('passport') && formatDate(formData.expiryDate) !== formatDate(currentUser.passportExpiryDate)) {
        updates.push({
          fieldLabel: t('passportExpiryDate'),
          currentValue: formatDate(currentUser.passportExpiryDate),
          newValue: formatDate(formData.expiryDate),
          isReplacing: !!currentUser.passportExpiryDate,
        });
      } else if ((typeName.includes('emirates') || typeName.includes('eid')) && formatDate(formData.expiryDate) !== formatDate(currentUser.emiratesIdExpiryDate)) {
        updates.push({
          fieldLabel: t('emiratesIdExpiryDate'),
          currentValue: formatDate(currentUser.emiratesIdExpiryDate),
          newValue: formatDate(formData.expiryDate),
          isReplacing: !!currentUser.emiratesIdExpiryDate,
        });
      } else if ((typeName.includes('labour') || typeName.includes('labor')) && formatDate(formData.expiryDate) !== formatDate(currentUser.labourCardExpiryDate)) {
        updates.push({
          fieldLabel: t('labourCardExpiryDate'),
          currentValue: formatDate(currentUser.labourCardExpiryDate),
          newValue: formatDate(formData.expiryDate),
          isReplacing: !!currentUser.labourCardExpiryDate,
        });
      } else if (typeName.includes('visa') && formatDate(formData.expiryDate) !== formatDate(currentUser.visaExpiryDate)) {
        updates.push({
          fieldLabel: t('visaExpiryDate'),
          currentValue: formatDate(currentUser.visaExpiryDate),
          newValue: formatDate(formData.expiryDate),
          isReplacing: !!currentUser.visaExpiryDate,
        });
      }
    }

    // OCR extracted fields
    if (ocrResult?.extractedNationality && ocrResult.extractedNationality !== currentUser.nationality?.name) {
      updates.push({
        fieldLabel: t('nationality'),
        currentValue: currentUser.nationality?.name || '',
        newValue: ocrResult.extractedNationality,
        isReplacing: !!currentUser.nationality,
      });
    }

    if (ocrResult?.extractedDateOfBirth) {
      const currentBirth = apiDateOnlyParam(currentUser.birthDate);
      if (ocrResult.extractedDateOfBirth !== currentBirth) {
        updates.push({
          fieldLabel: t('Birth Date'),
          currentValue: currentBirth,
          newValue: ocrResult.extractedDateOfBirth,
          isReplacing: !!currentUser.birthDate,
        });
      }
    }

    if (ocrResult?.extractedGender) {
      const currentGender = currentUser.gender != null ? String(currentUser.gender) : '';
      if (ocrResult.extractedGender !== currentGender) {
        updates.push({
          fieldLabel: t('Gender'),
          currentValue: currentGender,
          newValue: ocrResult.extractedGender,
          isReplacing: currentUser.gender != null,
        });
      }
    }

    if (ocrResult?.extractedFullName && ocrResult.extractedFullName !== currentUser.fullName) {
      updates.push({
        fieldLabel: t('Full Name'),
        currentValue: currentUser.fullName || '',
        newValue: ocrResult.extractedFullName,
        isReplacing: !!currentUser.fullName,
      });
    }

    if (ocrResult?.extractedMaritalStatus) {
      const currentMarital = currentUser.maritalStatus != null ? String(currentUser.maritalStatus) : '';
      if (ocrResult.extractedMaritalStatus !== currentMarital) {
        updates.push({
          fieldLabel: t('Marital Status'),
          currentValue: currentMarital,
          newValue: ocrResult.extractedMaritalStatus,
          isReplacing: currentUser.maritalStatus != null,
        });
      }
    }

    if (ocrResult?.extractedPassportNumber && ocrResult.extractedPassportNumber !== currentUser.passportNumber) {
      // Only add if not already covered by form documentNumber above
      const alreadyCovered = typeName.includes('passport') && formData.documentNumber === ocrResult.extractedPassportNumber;
      if (!alreadyCovered) {
        updates.push({
          fieldLabel: t('passportNumber'),
          currentValue: currentUser.passportNumber || '',
          newValue: ocrResult.extractedPassportNumber,
          isReplacing: !!currentUser.passportNumber,
        });
      }
    }

    if (ocrResult?.extractedEmiratesIdNumber && ocrResult.extractedEmiratesIdNumber !== currentUser.emiratesIdNumber) {
      const alreadyCovered = (typeName.includes('emirates') || typeName.includes('eid')) && formData.documentNumber === ocrResult.extractedEmiratesIdNumber;
      if (!alreadyCovered) {
        updates.push({
          fieldLabel: t('emiratesIdNumber'),
          currentValue: currentUser.emiratesIdNumber || '',
          newValue: ocrResult.extractedEmiratesIdNumber,
          isReplacing: !!currentUser.emiratesIdNumber,
        });
      }
    }

    if (ocrResult?.extractedUnifiedNumber && ocrResult.extractedUnifiedNumber !== currentUser.uidNumber) {
      updates.push({
        fieldLabel: t('uidNumber'),
        currentValue: currentUser.uidNumber || '',
        newValue: ocrResult.extractedUnifiedNumber,
        isReplacing: !!currentUser.uidNumber,
      });
    }

    if (ocrResult?.extractedMohrePermitNumber && ocrResult.extractedMohrePermitNumber !== currentUser.molListNumber) {
      updates.push({
        fieldLabel: t('molListNumber'),
        currentValue: currentUser.molListNumber || '',
        newValue: ocrResult.extractedMohrePermitNumber,
        isReplacing: !!currentUser.molListNumber,
      });
    }

    return updates;
  }, [formData, ocrResult, currentUser, attachmentTypes, t]);

  const showForm = !!primaryFile || !!editData || skipFile;

  return (
    <Dialog open={open} onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {showForm ? (
            primaryFile ? (
              // Split pane: preview + form (file selected)
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Left: File preview */}
                <FilePreviewPanel
                  primaryFile={primaryFile}
                  additionalFiles={additionalFiles}
                  onAddFiles={handleFilesSelected}
                  onRemoveAdditional={handleRemoveAdditional}
                  onLabelChange={handleLabelChange}
                />
                {/* Right: Smart form + profile updates */}
                <div>
                  <DocumentFormPanel
                    formData={formData}
                    onChange={setFormData}
                    ocrResult={ocrResult}
                    analyzing={analyzing}
                  />
                  <ProfileUpdatesPanel
                    updates={profileUpdates}
                    skipProfileUpdates={skipProfileUpdates}
                    onSkipChange={setSkipProfileUpdates}
                  />
                </div>
              </div>
            ) : (
              // Single column: form only (no file / editing without new file)
              <div>
                <DocumentFormPanel
                  formData={formData}
                  onChange={setFormData}
                  ocrResult={ocrResult}
                  analyzing={analyzing}
                />
                <ProfileUpdatesPanel
                  updates={profileUpdates}
                  skipProfileUpdates={skipProfileUpdates}
                  onSkipChange={setSkipProfileUpdates}
                />
              </div>
            )
          ) : (
            // Initial state: drop zone + skip link
            <div>
              <FileDropZone
                onFilesSelected={handleFilesSelected}
                multiple
              />
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => setSkipFile(true)}
                  className="text-sm text-primary hover:underline"
                >
                  {t('skipFile')}
                </button>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('Cancel')}</Button>
          <Button loading={loading} onClick={handleSave} disabled={!formData.companyAttachmentTypeId || analyzing}>
            {t('Save Document')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentUploadModal;
